'use client';

import type { RefObject } from 'react';
import { createContext, useContext, useEffect, useRef } from 'react';

import { logger } from '@/shared/lib';
import type { AgentTweakableStateTool, AgentTweakableToolCall } from './AgentTweakableState';
import { AgentTweakableState } from './AgentTweakableState';

// == React bindings ==
const AgentTweakableStateContext = createContext<{
  prefix: string[];
  atsRef: RefObject<AgentTweakableState> | null;
}>({ prefix: [], atsRef: null });

export function AgentTweakableStateProvider(props: { children: React.ReactNode }) {
  const atsRef = useRef<AgentTweakableState>(new AgentTweakableState());

  useEffect(() => {
    logger.info('copilot.agentTweakable.provider.mount');
    return () => {
      logger.info('copilot.agentTweakable.provider.unmount');
    };
  }, []);

  return (
    <AgentTweakableStateContext.Provider value={{ prefix: [], atsRef }}>
      {props.children}
    </AgentTweakableStateContext.Provider>
  );
}

export function AgentTweakableArea(props: { prefix: string; children: React.ReactNode }) {
  const { prefix, atsRef } = useContext(AgentTweakableStateContext);
  if (!atsRef) {
    throw new Error('AgentTweakableState not found');
  }
  const nextPrefix = [...prefix, props.prefix];
  const prefixKey = nextPrefix.join('.');

  useEffect(() => {
    logger.info('copilot.agentTweakable.area.mount', { prefix: prefixKey });
    return () => {
      logger.info('copilot.agentTweakable.area.unmount', { prefix: prefixKey });
    };
  }, [prefixKey]);

  return (
    <AgentTweakableStateContext.Provider value={{ prefix: nextPrefix, atsRef }}>
      {props.children}
    </AgentTweakableStateContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAgentTweakable<T>(
  tool: AgentTweakableStateTool<T>,
  key: string,
  value: T,
  setValue: (value: T) => void
) {
  return useAgentTweakableInternal(tool, key, value, setValue);
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAgentVisible<T>(tool: AgentTweakableStateTool<T>, key: string, value: T) {
  return useAgentTweakableInternal(tool, key, value, null);
}

// Internal hook implementation - renamed to follow React naming convention
function useAgentTweakableInternal<T>(
  tool: AgentTweakableStateTool<T>,
  key: string,
  value: T,
  setValue: ((value: T) => void) | null
) {
  const { prefix, atsRef } = useContext(AgentTweakableStateContext);
  if (!atsRef) {
    throw new Error('AgentTweakableState not found');
  }
  const fullKey = [...prefix, key].join('.');
  useEffect(() => {
    const ats = atsRef.current;
    logger.debug('copilot.agentTweakable.item.register', {
      tool: tool.name,
      key: fullKey,
      settable: setValue != null,
      valueType: typeof value,
    });
    const deregisterCallback = ats.registerItem(tool, fullKey, value, setValue);
    return () => {
      logger.debug('copilot.agentTweakable.item.deregister', { tool: tool.name, key: fullKey });
      deregisterCallback();
    };
  }, [atsRef, tool, fullKey, value, setValue]);
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAgentTweakableTools(): {
  getFrontendTools: () => ReturnType<AgentTweakableState['getFrontendTools']>;
  getFrontendContext: () => ReturnType<AgentTweakableState['getFrontendContext']>;
  handleToolCall: (
    name: string,
    toolCall: AgentTweakableToolCall
  ) => ReturnType<AgentTweakableState['handleToolCall']>;
  tryHandleToolCall: (
    name: string,
    toolCall: AgentTweakableToolCall
  ) => ReturnType<AgentTweakableState['tryHandleToolCall']>;
} {
  const { atsRef } = useContext(AgentTweakableStateContext);
  if (!atsRef) {
    throw new Error('AgentTweakableState not found');
  }
  return {
    getFrontendTools: () => atsRef.current.getFrontendTools(),
    getFrontendContext: () => atsRef.current.getFrontendContext(),
    handleToolCall: (name: string, toolCall: AgentTweakableToolCall) =>
      atsRef.current.handleToolCall(name, toolCall),
    tryHandleToolCall: (name: string, toolCall: AgentTweakableToolCall) =>
      atsRef.current.tryHandleToolCall(name, toolCall),
  };
}
